import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, filter, from, map, race, take, timeout } from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';

import { DataspaceRoutingService } from '@core/services/dataspace-routing/dataspace-routing.service';
import { DataspaceSelectionService } from '@core/services/dataspace-selection/dataspace-selection.service';
import { NotificationService } from '@core/services/notification/notification.service';
import * as DataspaceActions from '@core/store/dataspace/dataspace.actions';
import {
  selectDataspaceErrorForId,
  selectDataspaceModel
} from '@core/store/dataspace/dataspace.selectors';

export const dataspaceGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const store = inject(Store);
  const dataspaceRoutingService = inject(DataspaceRoutingService);
  const notificationService = inject(NotificationService);
  const dataspaceSelection = inject(DataspaceSelectionService);

  const dataspaceId = route.paramMap.get(AppRoutes.dataspace.params.dataspaceId);
  if (!dataspaceId) {
    console.warn('DataspaceGuard: No dataspace ID found in route parameters.');
    return router.createUrlTree(['']);
  }

  const numericDataspaceId = parseInt(dataspaceId, 10);

  // Dispatch the fetchDataspace action to update store
  store.dispatch(DataspaceActions.fetchDataspace({ id: numericDataspaceId }));

  // Success stream: wait for matching dataspace
  const successStream$ = store.select(selectDataspaceModel).pipe(
    filter((dataspace) => dataspace?.id === numericDataspaceId),
    take(1),
    map(() => true)
  );

  // Error stream: wait for matching error
  const errorStream$ = store.select(selectDataspaceErrorForId(numericDataspaceId)).pipe(
    filter((error) => error !== undefined),
    take(1),
    map((error) => {
      // Throw error to be caught by catchError below
      throw error;
    })
  );

  // Race: whichever emits first wins
  return race(successStream$, errorStream$).pipe(
    timeout(5000), // Fallback timeout for unexpected scenarios
    catchError((error) => {
      console.error('DataspaceGuard: Error or timeout loading dataspace:', error);
      dataspaceSelection.forget(numericDataspaceId);

      // Determine error type and show appropriate message
      const isPermissionError = error?.httpStatus === 403;
      const isNotFoundError = error?.httpStatus === 404;
      const isTimeout = error?.name === 'TimeoutError';

      if (isPermissionError) {
        notificationService.error(
          'Access Denied',
          "You don't have access to this dataspace. Redirecting to your default dataspace."
        );
      } else if (isNotFoundError) {
        notificationService.error(
          'Dataspace Not Found',
          'The requested dataspace does not exist. Redirecting to your default dataspace.'
        );
      } else if (isTimeout) {
        notificationService.error(
          'Request Timeout',
          'The dataspace took too long to load. Redirecting to your default dataspace.'
        );
      } else {
        notificationService.error(
          'Error Loading Dataspace',
          'Unable to load the requested dataspace. Redirecting to your default dataspace.'
        );
      }

      // Redirect to default dataspace
      return from(dataspaceRoutingService.navigateToDefaultDataspace()).pipe(
        map(() => false)
      );
    })
  );
};
